/**
 * Agrupa instancias RANGE_ZIP contiguas en un bloque HTTP Range.
 * byteOffset/byteLength = payload Part-10 (no Local File Header).
 * El Range HTTP puede abarcar gaps LFH; el slice usa offsets de payload.
 */

import {
  MAX_COALESCED_BLOCK_SIZE_BYTES,
  MAX_GAP_THRESHOLD_BYTES,
  MAX_COALESCED_MEMBERS,
} from './r2ZipDownloadPrefs'

/**
 * @typedef {{
 *   slot: number,
 *   byteOffset: number,
 *   byteLength: number,
 *   seriesIdx: number,
 *   archiveUrl: string,
 * }} CoalesceCandidate
 *
 * @typedef {{
 *   slot: number,
 *   byteOffset: number,
 *   byteLength: number,
 *   localStart: number,
 * }} CoalesceMember
 *
 * @typedef {{
 *   rangeStart: number,
 *   rangeEnd: number,
 *   archiveUrl: string,
 *   seriesIdx: number,
 *   members: CoalesceMember[],
 * }} CoalescedBlock
 */

/**
 * @param {CoalesceCandidate} c
 * @returns {boolean}
 */
function isValidCandidate(c) {
  if (!c || typeof c !== 'object') return false
  if (typeof c.slot !== 'number' || !Number.isFinite(c.slot)) return false
  if (typeof c.byteOffset !== 'number' || !Number.isFinite(c.byteOffset) || c.byteOffset < 0) {
    return false
  }
  if (typeof c.byteLength !== 'number' || !Number.isFinite(c.byteLength) || c.byteLength <= 0) {
    return false
  }
  if (typeof c.seriesIdx !== 'number' || !Number.isFinite(c.seriesIdx)) return false
  if (!c.archiveUrl || typeof c.archiveUrl !== 'string') return false
  return true
}

/**
 * Gap entre fin de A y inicio de B (payload).
 * @param {{ byteOffset: number, byteLength: number }} a
 * @param {{ byteOffset: number, byteLength: number }} b
 */
export function payloadGapBytes(a, b) {
  return b.byteOffset - (a.byteOffset + a.byteLength)
}

/**
 * Construye un bloque coalesced desde un seed y candidatos abiertos.
 * Solo misma serie + mismo archiveUrl.
 * Por defecto solo extiende hacia offsets mayores (adelante en el stack).
 *
 * @param {CoalesceCandidate} seed
 * @param {CoalesceCandidate[]} openCandidates — slots abiertos (incluye seed)
 * @param {{
 *   maxBlockBytes?: number,
 *   maxGapBytes?: number,
 *   maxMembers?: number,
 *   preferForward?: boolean,
 * }} [opts]
 * @returns {CoalescedBlock|null}
 */
export function buildCoalescedBlock(seed, openCandidates, opts = {}) {
  if (!isValidCandidate(seed)) return null

  const maxBlockBytes =
    opts.maxBlockBytes != null && Number.isFinite(Number(opts.maxBlockBytes))
      ? Math.max(1, Number(opts.maxBlockBytes))
      : MAX_COALESCED_BLOCK_SIZE_BYTES
  const maxGapBytes =
    opts.maxGapBytes != null && Number.isFinite(Number(opts.maxGapBytes))
      ? Math.max(0, Number(opts.maxGapBytes))
      : MAX_GAP_THRESHOLD_BYTES
  const maxMembers =
    opts.maxMembers != null && Number.isFinite(Number(opts.maxMembers))
      ? Math.max(1, Math.floor(Number(opts.maxMembers)))
      : MAX_COALESCED_MEMBERS
  const preferForward = opts.preferForward !== false

  const same = (Array.isArray(openCandidates) ? openCandidates : [])
    .filter(isValidCandidate)
    .filter(
      (c) =>
        c.seriesIdx === seed.seriesIdx &&
        c.archiveUrl === seed.archiveUrl
    )
    .sort((a, b) => {
      if (a.byteOffset !== b.byteOffset) return a.byteOffset - b.byteOffset
      return a.slot - b.slot
    })

  if (!same.length) return null

  const seedInList = same.find((c) => c.slot === seed.slot)
  if (!seedInList) {
    same.push(seed)
    same.sort((a, b) => {
      if (a.byteOffset !== b.byteOffset) return a.byteOffset - b.byteOffset
      return a.slot - b.slot
    })
  }

  const seedIdx = same.findIndex((c) => c.slot === seed.slot)
  if (seedIdx < 0) return null

  /** @type {CoalesceCandidate[]} */
  const members = [same[seedIdx]]

  const canAdd = (nextOrPrev, asEnd) => {
    if (members.length >= maxMembers) return false
    const rangeStart = asEnd ? members[0].byteOffset : nextOrPrev.byteOffset
    const rangeEnd = asEnd
      ? nextOrPrev.byteOffset + nextOrPrev.byteLength - 1
      : members[members.length - 1].byteOffset +
        members[members.length - 1].byteLength -
        1
    return rangeEnd - rangeStart + 1 <= maxBlockBytes
  }

  // Extender hacia la derecha (offsets mayores) — preferido para scroll
  for (let i = seedIdx + 1; i < same.length; i++) {
    const prev = members[members.length - 1]
    const next = same[i]
    const gap = payloadGapBytes(prev, next)
    if (gap < 0) break
    if (gap > maxGapBytes) break
    if (!canAdd(next, true)) break
    members.push(next)
  }

  // Extender hacia la izquierda solo si no preferimos solo forward
  if (!preferForward) {
    for (let i = seedIdx - 1; i >= 0; i--) {
      const next = members[0]
      const prev = same[i]
      const gap = payloadGapBytes(prev, next)
      if (gap < 0) break
      if (gap > maxGapBytes) break
      if (!canAdd(prev, false)) break
      members.unshift(prev)
    }
  }

  const rangeStart = members[0].byteOffset
  const last = members[members.length - 1]
  const rangeEnd = last.byteOffset + last.byteLength - 1

  return {
    rangeStart,
    rangeEnd,
    archiveUrl: seed.archiveUrl,
    seriesIdx: seed.seriesIdx,
    members: members.map((m) => ({
      slot: m.slot,
      byteOffset: m.byteOffset,
      byteLength: m.byteLength,
      localStart: m.byteOffset - rangeStart,
    })),
  }
}

/**
 * Extrae un Part-10 del bloque.
 * @param {ArrayBuffer} blockBuffer
 * @param {{ localStart: number, byteLength: number }} member
 * @returns {ArrayBuffer}
 */
export function slicePart10FromBlock(blockBuffer, member) {
  const start = Math.max(0, Math.floor(member.localStart))
  const len = Math.max(0, Math.floor(member.byteLength))
  const end = Math.min(blockBuffer.byteLength, start + len)
  return blockBuffer.slice(start, end)
}
